import { useState, useEffect, useRef } from 'react';
import SectionHeader from '../components/SectionHeader';
import styles from './Playground.module.css';

const GRAVITY = 0.6;
const JUMP_STRENGTH = -10;
const MAX_JUMP_HEIGHT = 100;

function DevRunnerGame() {
  const [obstacles, setObstacles] = useState([]);
  const [score, setScore] = useState(0);
  const [playerY, setPlayerY] = useState(0);
  const [, setVelocityY] = useState(0);
  const [isJumping, setIsJumping] = useState(false);
  const [isHoldingJump, setIsHoldingJump] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const playerRef = useRef();
  const gameIntervalRef = useRef();
  const physicsIntervalRef = useRef();
  const collisionIntervalRef = useRef();

  const startGame = () => {
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setObstacles([]);
    setPlayerY(0);
    setVelocityY(0);
    setIsJumping(false);
    setIsHoldingJump(false);
  };

  const resetGame = () => {
    setGameStarted(false);
    setGameOver(false);
    setScore(0);
    setObstacles([]);
    setPlayerY(0);
    setVelocityY(0);
    setIsJumping(false);
    setIsHoldingJump(false);
  };

  // Spawn obstacles periodically
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    gameIntervalRef.current = setInterval(() => {
      const id = Date.now();
      const type = Math.random() > 0.7 ? 'powerup' : 'obstacle';
      
      setObstacles(obs => [...obs, { id, type }]);
      
      // Remove obstacle after animation completes
      setTimeout(() => {
        setObstacles(obs => obs.filter(o => o.id !== id));
        if (type === 'obstacle') {
          setScore(s => s + 1);
        }
      }, 3000);
    }, 1800);

    return () => clearInterval(gameIntervalRef.current);
  }, [gameStarted, gameOver]);

  // Física do salto com gravidade (como no jogo do Dino)
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    physicsIntervalRef.current = setInterval(() => {
      setVelocityY(vy => {
        let newVy = vy;
        
        // Se está subindo e segurando o botão, aplica menos gravidade (salto mais longo)
        if (newVy < 0 && isHoldingJump) {
          newVy += GRAVITY * 0.5; // Gravidade reduzida ao segurar
        } else {
          newVy += GRAVITY; // Gravidade normal
        }
        
        setPlayerY(y => {
          const newY = y + newVy;
          
          // Limita altura máxima
          if (newY <= -MAX_JUMP_HEIGHT) {
            setIsHoldingJump(false); // Para de aplicar boost
            return -MAX_JUMP_HEIGHT;
          }
          
          // Colisão com o chão
          if (newY >= 0) {
            setIsJumping(false);
            setIsHoldingJump(false);
            return 0;
          }
          
          return newY;
        });
        
        return newVy;
      });
    }, 1000 / 60); // 60 FPS

    return () => clearInterval(physicsIntervalRef.current);
  }, [gameStarted, gameOver, isHoldingJump]);

  // Handle jump on key press or click
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const onKeyDown = e => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        if (!isJumping) {
          setVelocityY(JUMP_STRENGTH);
          setIsJumping(true);
          setIsHoldingJump(true);
        }
      }
    };

    const onKeyUp = e => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        setIsHoldingJump(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [gameStarted, gameOver, isJumping]);

  const handleMouseDown = () => {
    if (!gameStarted || gameOver || isJumping) return;
    setVelocityY(JUMP_STRENGTH);
    setIsJumping(true);
    setIsHoldingJump(true);
  };

  const handleMouseUp = () => {
    setIsHoldingJump(false);
  };

  // Collision detection
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    collisionIntervalRef.current = setInterval(() => {
      const player = playerRef.current;
      if (!player) return;

      const playerRect = player.getBoundingClientRect();
      const obstacleElements = document.getElementsByClassName(styles.obstacle);
      const powerupElements = document.getElementsByClassName(styles.powerup);

      // Check obstacle collisions - melhorada para evitar falsos positivos
      for (let obs of obstacleElements) {
        const obsRect = obs.getBoundingClientRect();
        
        // Colisão mais precisa: verifica se há sobreposição real
        const horizontalOverlap = 
          obsRect.left < playerRect.right - 15 &&
          obsRect.right > playerRect.left + 15;
        
        const verticalOverlap = 
          obsRect.top < playerRect.bottom - 10 &&
          obsRect.bottom > playerRect.top + 10;
        
        if (horizontalOverlap && verticalOverlap) {
          setGameOver(true);
          clearInterval(gameIntervalRef.current);
          clearInterval(collisionIntervalRef.current);
          return;
        }
      }

      // Check powerup collisions - não adiciona aos bugs avoided
      for (let pwu of powerupElements) {
        const pwuRect = pwu.getBoundingClientRect();
        if (
          pwuRect.left < playerRect.right - 10 &&
          pwuRect.right > playerRect.left + 10 &&
          pwuRect.bottom > playerRect.top + 10 &&
          pwu.style.display !== 'none'
        ) {
          // Apenas aumenta score sem afetar bugs avoided
          pwu.style.display = 'none';
        }
      }
    }, 50);

    return () => clearInterval(collisionIntervalRef.current);
  }, [gameStarted, gameOver]);

  return (
    <div className={styles.gameWrapper}>
      {!gameStarted ? (
        <div className={styles.startScreen}>
          <h2>DevRunner</h2>
          <p>Press SPACE or TAP to jump over bugs!</p>
          <p>Collect ☕ coffee for bonus points</p>
          <button onClick={startGame} className={styles.startBtn}>
            Start Game
          </button>
        </div>
      ) : (
        <>
          <div 
            className={styles.gameContainer} 
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            role="button"
            tabIndex={0}
          >
            <div className={styles.score}>Bugs Avoided: {score}</div>
            <div 
              ref={playerRef} 
              className={styles.player}
              style={{ bottom: `${Math.abs(playerY)}px` }}
            >
              🤖
            </div>
            {obstacles.map(o => (
              <div 
                key={o.id} 
                className={o.type === 'obstacle' ? styles.obstacle : styles.powerup}
              >
                {o.type === 'obstacle' ? '🐛' : '☕'}
              </div>
            ))}
          </div>
          
          {gameOver && (
            <div className={styles.gameOverScreen}>
              <h2>Game Over!</h2>
              <p>Final Score: {score}</p>
              <button onClick={resetGame} className={styles.startBtn}>
                Play Again
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Playground() {
  return (
    <div className={styles.playground}>
      <SectionHeader 
        title="Playground" 
        subtitle="Interactive mini-game and experiments"
      />
      
      <div className={styles.description}>
        <p>
          Welcome to the DevRunner game! A Chrome Dino-inspired endless runner 
          with a developer twist. Avoid bugs and collect coffee to boost your score.
        </p>
        <p className={styles.instructions}>
          <strong>Controls:</strong> Press <kbd>SPACE</kbd> or tap/click anywhere to jump
        </p>
      </div>

      <DevRunnerGame />

      <div className={styles.footer}>
        <p>More interactive projects and experiments coming soon!</p>
      </div>
    </div>
  );
}

export default Playground;
